import React, { useEffect, useState } from 'react';
import { compose } from 'ramda';
import withMeta from 'hoc/withMeta';
import withCaseInfo from 'hoc/withCaseInfo';
import Dashboard from 'components/Layout/Dashboard/Dashboard';
import { Loader } from 'components/Base';
import Router from 'components/Router/Router';
import Store from 'store/store';
import api from 'api';
import { PolicyType } from 'store/modules/Policies/types';
import PortfolioBuilder from 'components/PortfolioBuilder/PortfolioBuilder';
import { FundAllocationType } from 'store/modules/ExistingPolicies/types';
import Forms from 'components/Forms';
import { Notify, formatPercent, unFormatPercent } from 'utils';
import { initialiseOverrides } from 'components/Policies/utils';
import { mapCipOptions } from 'components/InvestmentAnalyser/utils';

const { connect, actions, selectors } = Store;
const { useParams, useHistory } = Router;

interface PortfolioBuilderPageProps {
  setPolicy: (policy: PolicyType) => void;
  setRiskSummary: (riskProfile: any) => void;
  policy: PolicyType;
  cleanState: () => void;
  getPortfolioBuilderData: any;
  updateCipOptions: Function;
  updatePolicy: Function;
  setRecommendedRiskAnalysis: Function;
}
const PortfolioBuilderPage = ({
  getPortfolioBuilderData,
  policy,
  cleanState,
  updateCipOptions,
  updatePolicy,
  setRecommendedRiskAnalysis
}: PortfolioBuilderPageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const { portfolio, policyId } = useParams() as any;
  const history = useHistory();
  const isRetaintActionApplied = policy.performed_action === 1;

  useEffect(() => {
    setIsLoading(true);
    getPortfolioBuilderData({ portfolio, policyId })
      .then(() => {
        api.getCipRelations({ cip_thresholds: null }).then(({ data }) => {
          updateCipOptions(mapCipOptions(data));
          setIsLoading(false);
        });
      })
      .catch(() => setIsLoading(false));
    return () => cleanState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submitValues = ({ fund_allocations }: { fund_allocations: any }) => {
    if (isRetaintActionApplied) {
      return history.push(`/${portfolio}/recommendations`);
    }
    const filterNonZeroFund = fund_allocations.filter(
      (fundInstance: FundAllocationType) =>
        Number(unFormatPercent(fundInstance.allocation)) !== 0
    );
    const preparedFunds = filterNonZeroFund.map(
      (fundInstance: FundAllocationType) => ({
        fund: fundInstance.fund,
        allocation: (
          Number(unFormatPercent(fundInstance.allocation)) / 100
        ).toFixed(4),
        model_portfolio: fundInstance.model_portfolio,
        fund_charge_overrides: fundInstance.fund_charge_overrides,
        source_value: 2
      })
    );

    const policyData = {
      ...policy,
      fund_allocations: [...preparedFunds]
    };

    const preparedPolicy =
      Forms.Utils.prepareSubmit.portfolioBuilder(policyData);

    return api
      .updatePolicy({ portfolio, policyId, policyData: { ...preparedPolicy } })
      .then(({ data }: any) => {
        updatePolicy(data);
        api.getRiskAnalysisData({ portfolio }).then(response => {
          setRecommendedRiskAnalysis(response.data);
        });
        history.push(`/${portfolio}/recommendations`);
      })
      .catch((err: any) => {
        Notify.error(err.response.data.message);
      });
  };

  const initialValues = {
    fund_allocations: policy.fund_allocations.map((fundInstance: any) => {
      return {
        fund: fundInstance.fund,
        existing_weighting: fundInstance.retained_allocation
          ? Number(fundInstance.retained_allocation) * 100
          : 0,
        allocation: formatPercent(Number(fundInstance.allocation) * 100),
        model_portfolio: fundInstance.model_portfolio,
        fund_charge_overrides: initialiseOverrides(fundInstance)
      };
    }),
    wrap_retained: !!policy.wrap_retained && !!policy.wrap_percentage
  };
  if (policy.wrap_percentage) {
    initialValues.fund_allocations.push({
      fund: {
        name: 'Wrap Cash',
        active: true
      },
      existing_weighting: Number(policy.wrap_retained) * 100,
      allocation: formatPercent(Number(policy.wrap_percentage) * 100),
      model_portfolio: null,
      fund_charge_overrides: []
    });
  }

  return (
    <Dashboard>
      <Loader isLoading={isLoading}>
        <PortfolioBuilder
          onSubmit={submitValues}
          initialValues={initialValues}
        />
      </Loader>
    </Dashboard>
  );
};

export default compose(
  connect(
    (state: any) => ({
      policy: selectors.portfolioBuilderSelectors.policy(state)
    }),

    {
      getPortfolioBuilderData:
        actions.portfolioBuilderActions.getPortfolioBuilderData,
      cleanState: actions.portfolioBuilderActions.cleanState,
      updateCipOptions: actions.investmentAnalyserActions.updateCipOptions,
      updatePolicy: actions.caseActions.updatePolicies,
      setRecommendedRiskAnalysis: actions.caseActions.setRecommendedRiskAnalysis
    }
  ),
  withMeta,
  withCaseInfo
)(PortfolioBuilderPage);
